import { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';

const CartContext = createContext();

// Actions
const ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART',
};

// Load cart from localStorage
const loadCartFromStorage = () => {
  try {
    const stored = localStorage.getItem('fk_cart');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save cart to localStorage
const saveCartToStorage = (items) => {
  try {
    localStorage.setItem('fk_cart', JSON.stringify(items));
  } catch (e) {
    console.error('Failed to save cart:', e);
  }
};

// Reducer
const cartReducer = (state, action) => {
  let newItems;

  switch (action.type) {
    case ACTIONS.LOAD_CART:
      return { ...state, items: action.payload, loaded: true };

    case ACTIONS.ADD_ITEM: {
      const { product, quantity = 1 } = action.payload;
      const existingIndex = state.items.findIndex(item => item.id === product.id);

      if (existingIndex >= 0) {
        // Update quantity
        newItems = state.items.map((item, idx) =>
          idx === existingIndex
            ? { ...item, quantity: Math.min(item.quantity + quantity, item.stock || 99) }
            : item
        );
      } else {
        // Add new item
        const newItem = {
          id: product.id,
          name: product.name,
          slug: product.slug,
          price: product.price,
          discount_price: product.discount_price,
          image: product.images?.[0]?.image_url || product.images?.[0] || '/images/placeholder.png',
          stock: product.stock_quantity || product.stock || 99,
          quantity: Math.min(quantity, product.stock_quantity || product.stock || 99),
        };
        newItems = [...state.items, newItem];
      }
      saveCartToStorage(newItems);
      return { ...state, items: newItems };
    }

    case ACTIONS.REMOVE_ITEM:
      newItems = state.items.filter(item => item.id !== action.payload);
      saveCartToStorage(newItems);
      return { ...state, items: newItems };

    case ACTIONS.UPDATE_QUANTITY:
      newItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: Math.max(1, Math.min(action.payload.quantity, item.stock || 99)) }
          : item
      );
      saveCartToStorage(newItems);
      return { ...state, items: newItems };

    case ACTIONS.CLEAR_CART:
      saveCartToStorage([]);
      return { ...state, items: [] };

    default:
      return state;
  }
};

// Provider
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [], loaded: false });

  // Load cart on mount
  useEffect(() => {
    const items = loadCartFromStorage();
    dispatch({ type: ACTIONS.LOAD_CART, payload: items });
  }, []);

  const addToCart = useCallback((product, quantity = 1) => {
    const availableStock = Number(product.stock_quantity ?? product.stock ?? 0);
    const isOutOfStock = (product.stock_status === 'out_of_stock') || availableStock <= 0;

    if (isOutOfStock) {
      toast.error(`${product.name} is out of stock`, {
        autoClose: 1500,
        hideProgressBar: true,
      });
      return;
    }

    dispatch({ type: ACTIONS.ADD_ITEM, payload: { product, quantity } });
    toast.success(`${product.name} added to cart!`, {
      autoClose: 1500,
      hideProgressBar: true,
    });
  }, []);

  const removeFromCart = useCallback((itemId) => {
    dispatch({ type: ACTIONS.REMOVE_ITEM, payload: itemId });
    toast.info('Item removed from cart', { autoClose: 1500, hideProgressBar: true });
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    dispatch({ type: ACTIONS.UPDATE_QUANTITY, payload: { id, quantity } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_CART });
  }, []);

  // Computed values
  const cartCount = useMemo(() =>
    state.items.reduce((sum, item) => sum + item.quantity, 0),
    [state.items]
  );

  const cartTotal = useMemo(() =>
    state.items.reduce((sum, item) => {
      const price = item.discount_price || item.price;
      return sum + (price * item.quantity);
    }, 0),
    [state.items]
  );

  const getItemPrice = useCallback((item) => {
    return item.discount_price || item.price;
  }, []);

  const isInCart = useCallback((productId) => {
    return state.items.some(item => item.id === productId);
  }, [state.items]);

  const value = useMemo(() => ({
    items: state.items,
    loaded: state.loaded,
    cartCount,
    cartTotal,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getItemPrice,
    isInCart,
  }), [state.items, state.loaded, cartCount, cartTotal, addToCart, removeFromCart, updateQuantity, clearCart, getItemPrice, isInCart]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
